import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Divider,
  InputAdornment,
  MenuItem,
} from '@mui/material';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import BenefitCard from '../../../v2/Benefits/Beneficiary/Components/BenefitCard';
import FirmDetails from '../../../v2/Benefits/Beneficiary/Components/FirmDetails';
import {
  CustomButton,
  CustomCheckbox,
  CustomRadioGroup,
  StandardChip,
  TextField,
  ToggleSwitch,
  CustomSelect,
} from '../../../v2/MUI_custom_component';
import {
  AddShopInsuranceAction,
  getShopInsuranceInfoAction,
  updateShopInsuranceAction,
} from './APIs/action';

const getFieldComponent = (
  field,
  values,
  setValues,
  disabled = false,
  validationErrors = {},
  shop_details = null,
  shopInfoData = null,
) => {
  if (field.dependsOn) {
    const [depId, depVal] = field.dependsOn.split(':');
    if (!values.hasOwnProperty(depId) || String(values[depId]) !== depVal) {
      return null;
    }
  }

  const handleChange = (value) => {
    setValues((prev) => {
      const newValues = { ...prev, [field.id]: value };
      if (field.type === 'boolean' && value === null) {
        const clearDependentFields = (changedFieldId) => {
          shop_details?.questions.forEach((section) => {
            section.fields.forEach((depField) => {
              if (
                depField.dependsOn &&
                depField.dependsOn === `${changedFieldId}:true`
              ) {
                newValues[depField.id] = null;
                clearDependentFields(depField.id);
              }
            });
          });
        };
        clearDependentFields(field.id);
      }

      return newValues;
    });
  };

  if (field.id === 'q2') {
    let bankOptions = shopInfoData?.popular_banks_dropdown;
    if (!Array.isArray(bankOptions)) return null;

    if (values[field.id] === 'OTHER') {
      const getPlaceholder = () => 'Enter Bank Name';
      const getSlotProps = () => ({});
      const handleTextFieldChange = (e) => {
        let value = e.target.value;
        if (value.length > 60) {
          value = value.substring(0, 60);
        }
        setValues((prev) => ({ ...prev, q2_other: value }));
      };

      return (
        <>
          <TextField
            key={field.id + '_other'}
            label={'Custom Bank Name'}
            placeholder={getPlaceholder()}
            value={values.q2_other || ''}
            onChange={handleTextFieldChange}
            disabled={disabled}
            error={!!validationErrors.q2_other}
            helperText={validationErrors.q2_other}
            slotProps={getSlotProps()}
            inputProps={{ maxLength: 60 }}
          />
        </>
      );
    } else {
      // Check if the current value exists in bankOptions
      const currentValueExists = bankOptions.some(
        (option) => option.value === values[field.id],
      );

      const selectValue = currentValueExists ? values[field.id] : '';

      // Show only CustomSelect
      return (
        <CustomSelect
          key={field.id}
          label={field.label}
          placeholder='Select Bank'
          value={selectValue}
          onChange={(e) => {
            handleChange(e.target.value);
            if (e.target.value !== 'OTHER') {
              setValues((prev) => ({ ...prev, q2_other: '' }));
            }
          }}
          error={!!validationErrors[field.id]}
          helperText={validationErrors[field.id]}
          fullWidth
          displayEmpty
        >
          {!selectValue && (
            <MenuItem
              value=''
              disabled
            >
              <em>Select Bank</em>
            </MenuItem>
          )}

          {bankOptions.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
            >
              {option.label}
            </MenuItem>
          ))}
        </CustomSelect>
      );
    }
  }

  switch (field.type) {
    case 'boolean':
      return (
        <ToggleSwitch
          key={field.id}
          label={field.label}
          checked={!!values[field.id]}
          size='large'
          onChange={(e) => handleChange(e.target.checked ? true : null)}
          disabled={disabled}
        />
      );
    case 'textarea':
      return (
        <TextField
          key={field.id}
          label={field.label}
          value={values[field.id] || ''}
          onChange={(e) => handleChange(e.target.value)}
          multiline
          minRows={2}
          disabled={disabled}
          error={!!validationErrors[field.id]}
          helperText={validationErrors[field.id]}
        />
      );
    case 'textfield':
      const getPlaceholder = () => {
        if (field.id === 'q7') return 'Enter Amount';
        return '';
      };

      const getSlotProps = () => {
        if (field.id === 'q7') {
          return {
            input: {
              startAdornment: (
                <InputAdornment position='start'>
                  <CurrencyRupeeIcon sx={{ fontSize: '24px' }} />
                </InputAdornment>
              ),
            },
          };
        }
        return {};
      };

      const handleTextFieldChange = (e) => {
        let value = e.target.value;
        if (field.id === 'q7') {
          value = value.replace(/[^0-9.]/g, '');

          const parts = value.split('.');
          if (parts.length > 2) {
            value = parts[0] + '.' + parts.slice(1).join('');
          }
          if (parts[1] && parts[1].length > 2) {
            value = parts[0] + '.' + parts[1].substring(0, 2);
          }
        }
        handleChange(value);
      };

      return (
        <TextField
          key={field.id}
          label={field.label}
          placeholder={getPlaceholder()}
          value={values[field.id] || ''}
          onChange={handleTextFieldChange}
          disabled={disabled}
          error={!!validationErrors[field.id]}
          helperText={validationErrors[field.id]}
          slotProps={getSlotProps()}
        />
      );
    case 'radio':
      return (
        <Box key={field.id}>
          <Typography
            variant='body2'
            sx={{ mb: 1, color: '#333333' }}
          >
            {field.label}
          </Typography>
          <CustomRadioGroup
            options={field.options}
            value={values[field.id] || ''}
            onChange={(e) => handleChange(e.target.value)}
            disabled={disabled}
            name={field.id}
          />
        </Box>
      );
    case 'multi-select-chip':
      return (
        <Box key={field.id}>
          <Typography
            variant='body1'
            sx={{ mb: 1, color: '#333333' }}
          >
            {field.label}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {field.options.map((option) => (
              <StandardChip
                key={option}
                label={option}
                selected={(values[field.id] || []).includes(option)}
                showDelete={(values[field.id] || []).includes(option)}
                onChange={(isSelected) => {
                  const currentValues = values[field.id] || [];
                  if (isSelected) {
                    handleChange([...currentValues, option]);
                  } else {
                    handleChange(currentValues.filter((v) => v !== option));
                  }
                }}
                captalise={false}
                disabled={disabled}
              />
            ))}
          </Box>
          {validationErrors[field.id] && (
            <Typography
              variant='caption'
              color='error'
              sx={{ mt: 0.5, display: 'block' }}
            >
              {validationErrors[field.id]}
            </Typography>
          )}
        </Box>
      );
    case 'checkbox':
      return (
        <CustomCheckbox
          key={field.id}
          label={field.label}
          checked={!!values[field.id]}
          onChange={(e) => handleChange(e.target.checked)}
          disabled={disabled}
        />
      );
    default:
      return null;
  }
};

const ShopInsurance = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [shopInfoData, setShopInfoData] = useState();
  const [formValues, setFormValues] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [validationErrors, setValidationErrors] = useState({});
  const [loader, setLoader] = useState(false);

  const {
    benefit_card,
    selected_firm,
    shop_details,
    confirmation_text,
    popular_banks_dropdown,
  } = shopInfoData || {};
  const {
    isEdit,
    shop_data: shopData,
    insurance_type: insuranceType,
    selectedCompanyEntity: selectedCompanyEntity,
  } = location?.state || {};

  
  useEffect(() => {
    if (isEdit && shopData) {
      const initialValues = {};
      shopData.forEach((section) => {
        section.fields?.forEach((field) => {
          if (field.id && field.value != null) {
            initialValues[field.id] = field.value;
          }
        });
      });
      setFormValues(initialValues);
    }
  }, [isEdit, shopData]);

  // Handle custom bank values - convert to OTHER mode if value doesn't exist in options
  useEffect(() => {
    if (shopInfoData?.popular_banks_dropdown && formValues.q2) {
      const bankOptions = shopInfoData.popular_banks_dropdown;
      const currentValueExists = bankOptions.some(
        (option) => option.value === formValues.q2,
      );

      if (!currentValueExists && formValues.q2 !== 'OTHER') {
        setFormValues((prev) => ({
          ...prev,
          q2: 'OTHER',
          q2_other: prev.q2,
        }));
      }
    }
  }, [shopInfoData?.popular_banks_dropdown, formValues.q2]);

  const validateDependentFields = () => {
    const errors = {};

    shop_details?.questions.forEach((section) => {
      section.fields.forEach((field) => {
        const shouldValidate = checkFieldValidation(field, section);

        if (shouldValidate) {
          const fieldValue = formValues[field.id];
          const isEmpty = checkFieldEmpty(field, fieldValue);

          if (isEmpty) {
            errors[field.id] = `${field.label} is required`;
          }
        }
      });
    });

    if (formValues.q2 === 'OTHER') {
      const q2OtherValue = formValues.q2_other;
      if (!q2OtherValue || q2OtherValue.trim() === '') {
        errors.q2_other = ' Bank Name is required';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const checkFieldValidation = (field, section) => {
    if (field.dependsOn) {
      const [depId, depVal] = field.dependsOn.split(':');
      return formValues[depId] && String(formValues[depId]) === depVal;
    }
    return false;
  };

  const checkFieldEmpty = (field, fieldValue) => {
    if (field.type === 'textfield' || field.type === 'textarea') {
      return !fieldValue || fieldValue.trim() === '';
    }
    if (field.type === 'boolean' || field.type === 'radio') {
      return fieldValue === null || fieldValue === undefined;
    }
    return false;
  };

  const getSectionsToDisplay = () => {
    return currentStep === 1
      ? shop_details?.questions.slice(0, 2)
      : shop_details?.questions.slice(2);
  };
  const handleSubmit = async () => {
    setValidationErrors({});
    if (!validateDependentFields()) return;
    if (currentStep === 1) {
      setCurrentStep(2);
      return;
    }
    if (!confirm) {
      setValidationErrors({ confirm: 'Please confirm the details to proceed' });
      return;
    }
    await submitForm();
  };

  const submitForm = async () => {
    setIsLoading(true);
    const payload = createPayload();
    const { error, message } = isEdit
      ? await updateShopInsuranceAction(payload)
      : await AddShopInsuranceAction(payload);
    setIsLoading(false);
    if (!error) {
      toast.success(message);
      navigate('/beneficiary/shop-details', { state: { insuranceType } });
    } else {
      toast.error(message);
    }
  };

  const createPayload = () => {
    const payload = shop_details?.questions.reduce((acc, section) => {
      const validFields = section.fields.filter((field) => {
        if (field.dependsOn) {
          const [depId, depVal] = field.dependsOn.split(':');
          return (
            formValues.hasOwnProperty(depId) &&
            String(formValues[depId]) === depVal
          );
        }
        return true;
      });
      acc[section.section_id] = validFields.map((field) => {
        let value = formValues[field.id] ?? field.value;
        if (field.id === 'q2' && value === 'OTHER') {
          value = formValues.q2_other || '';
        }
        return {
          id: field.id,
          label: field.label,
          value,
        };
      });
      return acc;
    }, {});
    payload.company_esi_id = selectedCompanyEntity;
    return payload;
  };
  const handleGoBack = () => setCurrentStep(1);

  const getShopInsuranceInfo = async () => {
    setLoader(true);
    const { data, error, message } = await getShopInsuranceInfoAction(selectedCompanyEntity);
    if (!error) {
      setShopInfoData(data);
    } else {
      toast.error(message);
    }
    setLoader(false);
  };

  const clearConfirmationError = () => {
    if (validationErrors.confirm) {
      setValidationErrors((prev) => {
        const { confirm, ...rest } = prev;
        return rest;
      });
    }
  };
  useEffect(() => {
    getShopInsuranceInfo();
  }, []);

  if (loader) {
    return (
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        <Typography
          variant='body2'
          color='text.secondary'
        >
          Loading...
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Grid
        container
        spacing={2}
        sx={{ alignItems: 'stretch', mb: 3 }}
      >
        <Grid
          item
          xs={12}
          sm={6}
        >
          <BenefitCard
            item={benefit_card}
            sx={{ height: '100%' }}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
        >
          <Box
            sx={{
              background: '#fff',
              p: 2,
              borderRadius: 3,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-evenly',
            }}
          >
            {selected_firm?.length > 0 ? (
              selected_firm.map((detail, index) => (
                <FirmDetails
                  key={detail?.id || index}
                  detail={detail}
                />
              ))
            ) : (
              <Typography
                variant='body2'
                color='text.secondary'
              >
                No firm details available
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={4}
      >
        <Grid
          item
          xs={12}
        >
          <Box>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant='h2'>{shop_details?.header_title}</Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Box>
              {getSectionsToDisplay()?.map((section) => {
                return (
                  <Box
                    key={section.section}
                    mb={4}
                  >
                    <Typography
                      variant='labelD'
                      sx={{ mb: 1 }}
                    >
                      {section.section}
                    </Typography>
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                    >
                      {section.fields.map((field) => (
                        <React.Fragment key={field.id}>
                          {getFieldComponent(
                            field,
                            formValues,
                            setFormValues,
                            false,
                            validationErrors,
                            shop_details,
                            shopInfoData,
                          )}
                        </React.Fragment>
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
            {currentStep === 2 && (
              <Box>
                <CustomCheckbox
                  label={confirmation_text.label}
                  onChange={(e) => {
                    setConfirm(e.target.checked);
                    if (e.target.checked) clearConfirmationError();
                  }}
                />
                {validationErrors.confirm && (
                  <Typography
                    variant='caption'
                    color='error'
                    sx={{ mt: 0.5, display: 'block' }}
                  >
                    {validationErrors.confirm}
                  </Typography>
                )}
              </Box>
            )}
            <Box
              mt={2}
              sx={{
                display: currentStep === 2 ? 'flex' : 'block',
                justifyContent: 'space-between',
              }}
            >
              {currentStep === 2 && (
                <CustomButton
                  variant='outlined'
                  color='primary'
                  onClick={handleGoBack}
                  disabled={isLoading}
                  label='Back'
                  sx={{ width: { xs: '48%', sm: 'auto' } }}
                />
              )}

              <CustomButton
                variant='filled'
                color='primary'
                onClick={handleSubmit}
                disabled={isLoading}
                label={
                  isLoading
                    ? 'Submitting...'
                    : currentStep === 1
                    ? 'Save & Continue'
                    : 'Save'
                }
                sx={{
                  width: {
                    xs: '48%',
                    sm: 'auto',
                  },
                }}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ShopInsurance;
